import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import AddTaskModal from "./AddTaskModal";
import {
  fetchTasks,
  addNewTask,
  fetchStudents,
  deleteTaskFromState,
  setSelectedTask,
} from "../../../state/reducers/dashboardSlice";
import { getUser } from "../../../services/user.services";
import { jwtDecode } from "jwt-decode";
import { deleteTask, EditTasks } from "../../../services/admin.services";
import { toggleToDoTaskModal } from "../../../state/reducers/applicationSlice";
import toast from "react-hot-toast";

function ToDoList() {
  const dispatch = useDispatch();
  const [user, setUser] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const isToDoTaskModalOpen = useSelector(
    (state) => state.application.isToDoTaskModalOpen
  );
  const [users, setUsers] = useState("");
  const tasks = useSelector((state) => state.dashboard.tasks);
  const students = useSelector((state) => state.dashboard.students);
  const admins = useSelector((state) => state.admin.admins);
  const token = localStorage.getItem("token");
  const decodedtoken = jwtDecode(token);

  // Pagination calculations
  const totalTasks = tasks.length;
  const totalPages = Math.ceil(totalTasks / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentTasks = tasks.slice(startIndex, endIndex);

  useEffect(() => {
    dispatch(fetchTasks());
    dispatch(fetchStudents());
  }, [dispatch]);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await getUser();
        const user =
          (Array.isArray(response.data.data) &&
            response.data.data.find((user) => user.id === decodedtoken.user_id)
              ?.username) ||
          "N/A";
        setUser(user);
        setUsers(response.data.data);
      } catch (error) {
        console.error("Error fetching user:", error);
      }
    };
    fetchUser();
  }, []);

  // Reset to first page when tasks change
  useEffect(() => {
    setCurrentPage(1);
  }, [tasks.length]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const [year, month, day] = dateStr.split("-");
    return `${day}-${month}-${year}`;
  };

  const handleEditTask = async (taskId) => {
    const taskToEdit = tasks.find((task) => task.id === taskId);
    dispatch(setSelectedTask(taskToEdit));
    dispatch(toggleToDoTaskModal(true));
  };

  const handleDeleteTask = async (taskId) => {
    const isConfirmed = window.confirm(
      "Are you sure you want to delete this task?"
    );
    if (isConfirmed) {
      try {
        await deleteTask(taskId);
        dispatch(fetchTasks());
        toast.success("Task deleted successfully!");
      } catch (error) {
        console.error("Error deleting task:", error);
        toast.error("Failed to delete task. Please try again.");
      }
    }
  };

  // Pagination handlers
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push("...");
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pageNumbers.push(1);
        pageNumbers.push("...");
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        pageNumbers.push(1);
        pageNumbers.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push("...");
        pageNumbers.push(totalPages);
      }
    }

    return pageNumbers;
  };

  // Priority badge component
  const PriorityBadge = ({ priority }) => {
    const colorClasses = {
      High: "bg-red-100 text-red-700 border-red-200",
      Medium: "bg-yellow-100 text-yellow-700 border-yellow-200",
      Low: "bg-green-100 text-green-700 border-green-200",
    };

    return (
      <span
        className={`px-2 py-1 rounded text-xs font-medium border ${
          colorClasses[priority] || "bg-gray-100 text-gray-700 border-gray-200"
        }`}
      >
        {priority || "N/A"}
      </span>
    );
  };

  return (
    <div className="flex flex-col h-[500px]">
      {/* Header */}
      <div
        className={`bg-white/90 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-10 ${
          isToDoTaskModalOpen ? "blur-sm" : ""
        }`}
      >
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center">
            <span className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 mr-3 ring-2 ring-indigo-50">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </span>
            <div>
              <h2 className="text-lg font-semibold text-slate-800">
                To Do Tasks
              </h2>
              <span className="text-xs font-medium text-amber-600">Pending</span>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            {/* Items per page selector */}
            <div className="flex items-center space-x-2">
              <label htmlFor="itemsPerPage" className="text-sm text-slate-600">
                Show:
              </label>
              <select
                id="itemsPerPage"
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
                className="border border-slate-300 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <span className="text-sm text-slate-600">per page</span>
            </div>
            <button
              onClick={() => dispatch(toggleToDoTaskModal(true))}
              className="flex items-center px-4 py-2 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white rounded-xl font-medium transition-colors duration-200 shadow-lg shadow-indigo-500/20"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 mr-1"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                  clipRule="evenodd"
                />
              </svg>
              Add a new Task
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="relative flex-grow overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 uppercase sticky top-0 z-10 border-y border-slate-200/80">
            <tr>
              <th className="p-3 text-left font-medium">S.No</th>
              <th className="p-3 text-left font-medium">Title</th>
              <th className="p-3 text-left font-medium">Name</th>
              <th className="p-3 text-left font-medium">Start Date</th>
              <th className="p-3 text-left font-medium">Priority</th>
              <th className="p-3 text-left font-medium">Tags</th>
              <th className="p-3 text-left font-medium">Assigned By</th>
              <th className="p-3 text-center font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentTasks.length > 0 ? (
              currentTasks.map((task, index) => (
                <tr
                  key={task.id || index}
                  className="border-b border-slate-100 hover:bg-slate-50/80 transition-colors duration-150"
                >
                  <td className="p-3 text-slate-700">
                    {startIndex + index + 1}
                  </td>
                  <td className="p-3 font-medium text-slate-800">
                    {task.title || "N/A"}
                  </td>
                  <td className="p-3 text-slate-700">
                    {(users &&
                      users?.find((student) => student.id === task.related_to)
                        ?.username) ||
                      "N/A"}
                  </td>
                  <td className="p-3 text-slate-700">
                    {formatDate(task.start_date)}
                  </td>
                  <td className="p-3">
                    <PriorityBadge priority={task.priority} />
                  </td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1">
                      {Array.isArray(task.tags) && task.tags.length > 0 ? (
                        task.tags.map((tag, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full text-xs border border-slate-200"
                          >
                            {tag}
                          </span>
                        ))
                      ) : (
                        <span className="text-slate-500">N/A</span>
                      )}
                    </div>
                  </td>
                  <td className="p-3 text-slate-700">
                    {(users &&
                      users?.filter((user) => user.id === task.assigned_by)[0]
                        ?.username) ||
                      "N/A"}
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-center space-x-2">
                      <button
                        className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors duration-150"
                        onClick={() => handleEditTask(task.id)}
                        title="Edit"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                      </button>
                      <button
                        className="p-1.5 text-rose-600 hover:bg-rose-50 rounded-md transition-colors duration-150"
                        onClick={() => handleDeleteTask(task.id)}
                        title="Delete"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="text-center py-6 text-slate-500">
                  No tasks found. Add a new task to get started.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Footer with Pagination */}
      <div className="bg-slate-50 px-4 py-3 border-t border-slate-200/80">
        <div className="flex flex-col sm:flex-row justify-between items-center space-y-3 sm:space-y-0">
          {/* Results info */}
          <div className="text-sm text-slate-500">
            Showing {totalTasks > 0 ? startIndex + 1 : 0} to{" "}
            {Math.min(endIndex, totalTasks)} of {totalTasks} task(s)
          </div>

          {/* Pagination controls */}
          {totalPages > 1 && (
            <div className="flex items-center space-x-1">
              {/* Previous button */}
              <button
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
                className="px-3 py-1 rounded-lg border border-slate-300 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                &laquo; Prev
              </button>

              {/* Page numbers */}
              {getPageNumbers().map((pageNumber, index) => (
                <React.Fragment key={index}>
                  {pageNumber === "..." ? (
                    <span className="px-2 py-1 text-slate-500">...</span>
                  ) : (
                    <button
                      onClick={() => handlePageClick(pageNumber)}
                      className={`px-3 py-1 rounded border text-sm transition-colors duration-150 ${
                        currentPage === pageNumber
                          ? "bg-indigo-600 text-white border-indigo-600"
                          : "border-slate-300 hover:bg-slate-100"
                      }`}
                    >
                      {pageNumber}
                    </button>
                  )}
                </React.Fragment>
              ))}

              {/* Next button */}
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className="px-3 py-1 rounded-lg border border-slate-300 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                Next &raquo;
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ToDoList;
